import { requestOpenRouterCompletion } from './openRouterClient'
import { extractPdfTextFromFile } from './pdfParser'
import { DEFAULT_UPLOAD_SOURCE } from './constants'
import type { OpenRouterSettings, RecordKind } from './types'

interface ParseUploadedPdfInput {
  file: File
  kindHint?: RecordKind
  openRouterSettings: OpenRouterSettings
  source?: string
  title?: string
  useOpenRouter: boolean
}

export interface ParsedUploadedPdf {
  detectedKind: RecordKind
  pageCount: number
  parseSummary: string
  source: string
  textPreview: string
  title: string
  truncated: boolean
}

const OPENROUTER_TEXT_LIMIT = 7_000
const PREVIEW_TEXT_LIMIT = 1_600

function normalizeDetectedKind(value: string): RecordKind | null {
  const normalized = value.trim().toLowerCase()

  if (normalized === 'lab') return 'Lab'
  if (normalized === 'imaging') return 'Imaging'
  if (normalized === 'visit note' || normalized === 'visit_note' || normalized === 'visitnote')
    return 'Visit Note'
  if (normalized === 'medication') return 'Medication'

  return null
}

interface OpenRouterRecordAnalysis {
  kind: RecordKind
  summary: string
}

function parseOpenRouterRecordAnalysis(raw: string): OpenRouterRecordAnalysis {
  const trimmed = raw.trim()
  if (!trimmed) {
    throw new Error('[OPENROUTER_ANALYSIS_EMPTY] OpenRouter analysis payload is empty.')
  }

  const unfenced = trimmed
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/, '')
    .trim()

  let payload: unknown
  try {
    payload = JSON.parse(unfenced)
  } catch {
    throw new Error(
      '[OPENROUTER_ANALYSIS_INVALID_JSON] OpenRouter analysis must be valid JSON with keys "kind" and "summary".'
    )
  }

  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      '[OPENROUTER_ANALYSIS_INVALID_SHAPE] OpenRouter analysis must be an object with keys "kind" and "summary".'
    )
  }

  const maybeKind = (payload as { kind?: unknown }).kind
  const maybeSummary = (payload as { summary?: unknown }).summary
  if (typeof maybeKind !== 'string' || typeof maybeSummary !== 'string') {
    throw new Error(
      '[OPENROUTER_ANALYSIS_INVALID_SHAPE] OpenRouter analysis must include string fields "kind" and "summary".'
    )
  }

  const normalizedKind = normalizeDetectedKind(maybeKind)
  if (!normalizedKind) {
    throw new Error(
      '[OPENROUTER_ANALYSIS_INVALID_KIND] OpenRouter analysis kind must be one of Lab, Imaging, Visit Note, Medication.'
    )
  }

  const summary = maybeSummary.replace(/\s+/g, ' ').trim()
  if (!summary) {
    throw new Error('[OPENROUTER_ANALYSIS_EMPTY_SUMMARY] OpenRouter analysis summary must not be empty.')
  }

  return {
    kind: normalizedKind,
    summary,
  }
}

async function buildOpenRouterAnalysis(
  extractedText: string,
  pageCount: number,
  title: string,
  kindHint: RecordKind | undefined,
  source: string,
  settings: OpenRouterSettings
): Promise<OpenRouterRecordAnalysis> {
  const promptText = extractedText.slice(0, OPENROUTER_TEXT_LIMIT)
  const response = await requestOpenRouterCompletion({
    apiKey: settings.apiKey.trim(),
    model: settings.model.trim(),
    messages: [
      {
        role: 'system',
        content: [
          'You are a medical-record parser for a local dashboard app.',
          'Return strict JSON only, no markdown and no extra keys.',
          'JSON shape: {"kind":"Lab|Imaging|Visit Note|Medication","summary":"..."}',
          'Summary must be 2-3 concise factual sentences.',
          'Use kind hint only when the document evidence supports it.',
          'If this is portal communication, message thread, or provider note text, use kind "Visit Note" unless biomarker tables dominate the content.',
        ].join(' '),
      },
      {
        role: 'user',
        content: [
          `Record title: ${title}`,
          `Record kind hint: ${kindHint ?? 'none'}`,
          `Record source: ${source}`,
          `Page count: ${pageCount}`,
          'Extracted PDF text:',
          promptText,
        ].join('\n'),
      },
    ],
  })

  return parseOpenRouterRecordAnalysis(response)
}

function deriveTitleFromFileName(fileName: string): string {
  const trimmed = fileName.trim()
  if (!trimmed) {
    throw new Error('[UPLOAD_TITLE_REQUIRED] Unable to derive record title from an empty file name.')
  }

  const withoutExtension = trimmed.replace(/\.[^.]+$/, '')
  const normalized = withoutExtension.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  if (!normalized) {
    throw new Error('[UPLOAD_TITLE_REQUIRED] Unable to derive record title from file name.')
  }

  return normalized
}

function resolveUploadTitle(overrideTitle: string | undefined, fileName: string): string {
  const normalizedOverride = typeof overrideTitle === 'string' ? overrideTitle.trim() : ''
  if (normalizedOverride) {
    return normalizedOverride
  }

  return deriveTitleFromFileName(fileName)
}

function resolveUploadSource(overrideSource: string | undefined): string {
  const normalizedOverride = typeof overrideSource === 'string' ? overrideSource.trim() : ''
  if (normalizedOverride) {
    return normalizedOverride
  }

  return DEFAULT_UPLOAD_SOURCE
}

export async function parseUploadedPdfRecord({
  file,
  kindHint,
  openRouterSettings,
  source,
  title,
  useOpenRouter,
}: ParseUploadedPdfInput): Promise<ParsedUploadedPdf> {
  const resolvedTitle = resolveUploadTitle(title, file.name)
  const resolvedSource = resolveUploadSource(source)
  const extraction = await extractPdfTextFromFile(file)
  const textPreview = extraction.text.slice(0, PREVIEW_TEXT_LIMIT)

  if (!useOpenRouter) {
    throw new Error(
      '[OPENROUTER_NOT_CONFIGURED] OpenRouter settings are required for deterministic parsing.'
    )
  }

  const apiKey = openRouterSettings.apiKey.trim()
  const model = openRouterSettings.model.trim()
  if (!apiKey || !model) {
    throw new Error(
      '[OPENROUTER_NOT_CONFIGURED] OpenRouter settings are required for deterministic parsing.'
    )
  }

  const analysis = await buildOpenRouterAnalysis(
    extraction.text,
    extraction.pageCount,
    resolvedTitle,
    kindHint,
    resolvedSource,
    openRouterSettings
  )

  return {
    detectedKind: analysis.kind,
    pageCount: extraction.pageCount,
    parseSummary: analysis.summary,
    source: resolvedSource,
    textPreview,
    title: resolvedTitle,
    truncated: extraction.truncated,
  }
}
